import React, { useState, useEffect, lazy, Suspense } from "react"; // eslint-disable-line
import PropTypes from "prop-types";
import { useMediaQuery } from "react-responsive";
import axios from "axios";

import ErrorHandling from "./ErrorHandling"; // eslint-disable-line
import Loading from "./Loading"; // eslint-disable-line

const ContentGrid = lazy(() => import("./ContentGrid")); // eslint-disable-line
const DesktopFilterBar = lazy(() => import("./DesktopFilterBar")); // eslint-disable-line
const FilterModal = lazy(() => import("./filterModal/FilterModal")); // eslint-disable-line
const SearchBar = lazy(() => import("./SearchBar")); // eslint-disable-line
const Pagination = lazy(() => import("./Pagination")); // eslint-disable-line

const App = ({ postType, locale }) => {
	const [loadError, setLoadError] = useState(false);
	const [contentToRender, setContentToRender] = useState([]);
	const [filtersArray, setFiltersArray] = useState([]);
	const [activeFilters, setActiveFilters] = useState(
		localStorage.getItem("savedFilters") &&
			JSON.parse(localStorage.getItem("savedFilters")).postType == postType
			? JSON.parse(localStorage.getItem("savedFilters")).savedFilters
			: []
	);
	const [showFilterModal, toggleShowFilterModal] = useState(false);
	const [currentPageNumber, setCurrentPageNumber] = useState(
		localStorage.getItem("currentPageNumber") &&
			JSON.parse(localStorage.getItem("currentPageNumber")).postType == postType
			? JSON.parse(localStorage.getItem("currentPageNumber")).pageNumber
			: 1
	);
	const [totalPages, setTotalPages] = useState([]);
	const [query, setQuery] = useState(
		localStorage.getItem("searchQuery") &&
			JSON.parse(localStorage.getItem("searchQuery")).postType == postType
			? JSON.parse(localStorage.getItem("searchQuery")).query
			: ""
	);

	let postsPerPage;
	if (postType === "artist") {
		postsPerPage = 16;
	} else if (postType === "program") {
		postsPerPage = 18;
	}

	const pageArray = (number) => {
		return [...Array(number + 1).keys()].slice(1);
		// this is a helper function for the Pagination Station
		// it creates an array of the total number of pages, so that we can map over it to display the pagination buttons.
	};

	let windowUrl = window.origin;

	if (locale == "fr") {
		windowUrl = window.origin + `/${locale}`;
	}

	async function getFiltersData() {
		const apiUrl = `${windowUrl}/wp-json/masc/v1/filters?type=${postType}`;
		const response = await axios.get(apiUrl);

		if (response.data.code === "no_filters_found") {
			setLoadError(true);
		} else {
			const filtersObject = response.data;
			let arr = [];
			for (let header in filtersObject) {
				arr.push({
					title: header,
					...filtersObject[header],
				});
			}
			arr = arr.filter((item) => item.filters);
			setFiltersArray(arr);
		}
	}

	async function getContentData() {
		const params = makeQueryParams(activeFilters);

		let apiUrl = `${windowUrl}/wp-json/masc/v1/${postType}s?page=${currentPageNumber}`;
		if (params && query) {
			apiUrl = `${windowUrl}/wp-json/masc/v1/${postType}s${params}&q=${query}&page=${currentPageNumber}`;
		} else if (params) {
			apiUrl = `${windowUrl}/wp-json/masc/v1/${postType}s${params}&page=${currentPageNumber}`;
		} else if (query) {
			apiUrl = `${windowUrl}/wp-json/masc/v1/${postType}s?q=${query}&page=${currentPageNumber}`;
		}
		const response = await axios.get(apiUrl);
		const pageCount = response.data.postCount;
		const content = response.data[`${postType}s`];
		if (response.data.code == `no_${postType}s_found`) {
			setContentToRender(response.data.code);
		} else {
			setContentToRender(content);
			setTotalPages(pageArray(Math.ceil(pageCount / postsPerPage)));
		}
		toggleShowFilterModal(false);
	}

	async function clearFilters() {
		const apiUrl = `${windowUrl}/wp-json/masc/v1/${postType}s`;
		const response = await axios.get(apiUrl);
		const pageCount = response.data.postCount;
		const content = response.data[`${postType}s`];
		if (response.data.code == `no_${postType}s_found`) {
			setContentToRender(response.data.code);
		} else {
			setContentToRender(content);
			setActiveFilters([]);
			setTotalPages(pageArray(Math.ceil(pageCount / postsPerPage)));
		}
		toggleShowFilterModal(false);
		setCurrentPageNumber(1);
		setQuery("");
		localStorage.removeItem("savedFilters");
		localStorage.removeItem("searchQuery");
		localStorage.removeItem("currentPageNumber");
	}

	function handleToggleModal() {
		if (!showFilterModal) {
			toggleShowFilterModal(true);
		} else {
			toggleShowFilterModal(false);
		}
	}

	/* the following function reduces the the active filter array to build an object named
	activeFiltersObject with the shape {category1: ['filter1', 'filter2'], category2: ['filter3'}.

	the original shape of active filters is an array where each index is an object with a
	category and a corresponding filter:
	activefilter = [
		{category: 'category1', filter: 'filter1'},
		{category: 'category 1', filter: 'filter2'},
		{category: 'category2', filter: 'filter3'},
		{category: 'category2', filter: 'filter4'}]

	the first reduce function is to remove double entries for categories, and to store
	all the filters as values of their corresponding categories. if it takes
	the example array above, it outputs:
	activeFiltersObject:
		{category1: ['filter1', 'filter2'], category2: ['filter3', 'filter4'}

	the next step is to take the activeFiltersObject, and to create a string: paramString.
	the first category must be preceded by a '?', and every subsequent category is chained
	with an '&'. categories and filters are separated by '=', and filters themselves are
	separated by commas. thus, the output of paraString would be:
	'?category1=filter1,filter2&category2=filter3,filter4

	finally, this function is called by getContentData, and concatenated to the locally
	scopped varaiable: apiUrl */

	function makeQueryParams(activeFilters) {
		if (activeFilters.length > 0) {
			const activeFiltersObject = activeFilters.reduce(
				(filtersObject, filter) => {
					if (filtersObject[filter.category]) {
						filtersObject[filter.category] = [
							...filtersObject[filter.category],
							filter.filter,
						];
						return filtersObject;
					} else {
						return {
							...filtersObject,
							[filter.category]: [filter.filter],
						};
					}
				},
				{}
			);
			return Object.keys(activeFiltersObject).reduce(
				(paramString, category) => {
					return paramString.concat(
						`${paramString ? "&" : "?"}${category}=${activeFiltersObject[
							category
						].toString()}`
					);
				},
				""
			);
		}
	}

	const savePageNumber = (num, postType) => {
		localStorage.setItem(
			"currentPageNumber",
			JSON.stringify({
				postType: postType,
				pageNumber: num,
			})
		);
	};

	function handleIncrementPage() {
		let newPageNumber = currentPageNumber + 1;
		if (currentPageNumber < totalPages.length) {
			setCurrentPageNumber(newPageNumber);
			savePageNumber(newPageNumber, postType);
		}
	}

	function handleDecrementPage() {
		let newPageNumber = currentPageNumber - 1;
		if (currentPageNumber > 1) {
			setCurrentPageNumber(newPageNumber);
			savePageNumber(newPageNumber, postType);
		}
	}

	const handleButtonPageSelect = (buttonNumber) => {
		setCurrentPageNumber(buttonNumber);
		savePageNumber(buttonNumber, postType);
	};

	const renderLoader = () => <Loading />;

	const isDesktopOrLaptop = useMediaQuery({
		query: "(min-width: 1224px)",
	});

	useEffect(() => {
		getFiltersData().catch(() => {
			setLoadError(true);
		});
	}, [locale]);

	useEffect(() => {
		getContentData().catch(() => {
			setLoadError(true);
			setActiveFilters([]);
		});
	}, [currentPageNumber, locale]);
	return (
		<>
			{loadError && <ErrorHandling loadError={loadError} />}

			<Suspense fallback={renderLoader()}>
				{!isDesktopOrLaptop && showFilterModal && (
					<FilterModal
						show={showFilterModal}
						filtersArray={filtersArray}
						handleToggleModal={handleToggleModal}
						activeFilters={activeFilters}
						setActiveFilters={setActiveFilters}
						getContentData={getContentData}
						clearFilters={clearFilters}
						setCurrentPageNumber={setCurrentPageNumber}
						locale={locale}
						postType={postType}
					/>
				)}
				<div
					className={`search-mobile-controller main-grid ${
						postType == "artist" ? "padding__top--small" : ""
					}`}
				>
					<div className="search-mobile-controller__margin"></div>
					<div className="search-mobile-controller__main">
						<div className="search-mobile-controller__main__wrapper">
							<SearchBar
								getContentData={getContentData}
								query={query}
								setQuery={setQuery}
								locale={locale}
								postType={postType}
							/>
							<div>
								<button
									className="filter-controls--white mobile-controller"
									onClick={() => handleToggleModal()}
								>
									{locale == "en_US" || locale == "en"
										? "View All Filters"
										: "Voir tous les filtres"}
								</button>
							</div>
							{activeFilters && activeFilters.length > 0 && (
								<div className="desktop-filter-bar__main__selected-filters mobile-controller padding__top--small">
									<p>
										{locale == "en_US" || locale == "en"
											? "Selected filters:"
											: "Filtres sélectionnés:"}
									</p>
									{activeFilters.map((filter, i) => (
										<span key={i} className="selected">
											{filter.title}
										</span>
									))}
								</div>
							)}
						</div>
					</div>

					<div className="search-mobile-controller__margin"></div>
				</div>
				{isDesktopOrLaptop && (
					<DesktopFilterBar
						filtersArray={filtersArray}
						showModal={showFilterModal}
						toggleShowFilterModal={toggleShowFilterModal}
						activeFilters={activeFilters}
						setActiveFilters={setActiveFilters}
						getContentData={getContentData}
						handleToggleModal={handleToggleModal}
						clearFilters={clearFilters}
						setCurrentPageNumber={setCurrentPageNumber}
						locale={locale}
						postType={postType}
					/>
				)}
				<ContentGrid
					content={contentToRender}
					postType={postType}
					locale={locale}
				/>
				{contentToRender !== `no_${postType}s_found` && (
					<Pagination
						handleDecrementPage={handleDecrementPage}
						handleIncrementPage={handleIncrementPage}
						totalPages={totalPages}
						handleButtonPageSelect={handleButtonPageSelect}
						currentPageNumber={currentPageNumber}
					/>
				)}
			</Suspense>
		</>
	);
};

App.propTypes = {
	postType: PropTypes.string,
};

export default App;
